/**
 * 실행계획의 「설명이 필요한 줄」 — 사례 본문·유의사항이 언급한 계획 줄(「Id 12」 「Id 6~8」 「Id 22·23·25」)을 찾아
 * 번호를 달고, 계획 표의 그 줄 끝에 `◀ [1]` 로 표시한다(2026-10-11, SQL 튜닝 리포트의 「◀ D1」 과 같은 뜻).
 *
 * 어느 계획의 줄인가 — 문장에 「개선 후·변경 후」 가 있으면 개선 후, 「개선 전·변경 전」 이면 개선 전. 없으면 문장이 든 절 제목
 * (분명한 말, 그다음 「개선 방안·근거」 → 개선 후 / 「원인·문제」 → 개선 전), 그것도 없으면 개선 전. 고른 계획에 그 Id 가 없고 다른 계획에 있으면 그쪽으로.
 * 판별 낱말은 화면 문구가 아니라 본문을 읽는 단서라 \u 로 적는다(i18n 가드 대상 아님).
 * 저장값은 바꾸지 않는다 — 보여 줄 때만.
 */
// 문장 안에서는 **분명한 말만** 계획을 고른다(개선 전/후 · 변경 전/후). 「결과」 「근거」 같은 말은 「중간 결과」 「개선 전 근거」 처럼
// 반대 뜻으로도 쓰여 문장에서는 쓰지 않는다 — 절 제목(문맥)에서만 쓴다(SOFT_*)
const HARD_AFTER = /\uAC1C\uC120\s*\uD6C4|\uBCC0\uACBD\s*\uD6C4|\bafter\b/i;
const HARD_BEFORE = /\uAC1C\uC120\s*\uC804|\uBCC0\uACBD\s*\uC804|\bbefore\b/i;
const SOFT_AFTER = /\uAC1C\uC120\s*\uBC29\uC548|\uADFC\uAC70/;
const SOFT_BEFORE = /\uC6D0\uC778|\uBB38\uC81C/;
const ID_RE = /\bId\s*(\d+)((?:\s*[~\-\u2013\u00B7,]\s*\d+)*)/gi;
const ROW_ID = /^\|\s*\*?\s*(\d+)\s*\|/;
const MAX_NOTE = 240;

/** 문장·제목에 분명히 적힌 쪽 — 둘 다 있거나 없으면 null */
function sideOf(line) {
  const a = HARD_AFTER.test(line);
  const b = HARD_BEFORE.test(line);
  return a && !b ? 'after' : (b && !a ? 'before' : null);
}

/** 「Id 6~8」 「Id 22·23·25」 「Id 7·13」 → [6,7,8] / [22,23,25] / [7,13] */
export function idsIn(line) {
  const out = [];
  ID_RE.lastIndex = 0;
  let m;
  while ((m = ID_RE.exec(String(line || ''))) !== null) {
    let prev = Number(m[1]);
    out.push(prev);
    const rest = m[2] || '';
    const re = /\s*([~\-–·,])\s*(\d+)/g;
    let r;
    while ((r = re.exec(rest)) !== null) {
      const n = Number(r[2]);
      if (/[~\-–]/.test(r[1]) && n > prev && n - prev <= 50) { for (let k = prev + 1; k <= n; k += 1) out.push(k); }
      else out.push(n);
      prev = n;
    }
  }
  return [...new Set(out)];
}

/** 계획 글의 표 줄 Id 집합 */
export function planIds(planText) {
  const s = new Set();
  for (const l of String(planText || '').split('\n')) {
    const m = ROW_ID.exec(l);
    if (m) s.add(Number(m[1]));
  }
  return s;
}

/**
 * @param texts  [{ text, defaultSide }] — 본문·유의사항(절 제목 # 을 문맥으로 쓴다)
 * @param plans  { before: 계획 글, after: 계획 글 }
 * @returns { notes: [{ no, text, side, ids }], marks: { before: Map<id, no[]>, after: Map<id, no[]> },
 *            issues: [{ kind, text, ids, side }] }
 *   issues — 편집 화면의 경고(2026-10-11). 표시가 조용히 빠지거나 엉뚱한 계획에 붙는 경우를 말한다:
 *     none    언급한 Id 가 어느 계획에도 없다(표시가 빠진다) — 계획을 바꾸고 본문 Id 를 그대로 둔 경우
 *     partial 일부 Id 만 있다(있는 줄에만 붙는다) — ids 는 없는 것들
 *     moved   문맥(문장·절 제목)은 한쪽인데 그 계획에 없어 다른 계획에 붙였다 — side 는 붙인 쪽
 */
export function planNotes(texts, plans) {
  const have = { before: planIds(plans.before), after: planIds(plans.after) };
  const marks = { before: new Map(), after: new Map() };
  const notes = [];
  const issues = [];
  for (const { text, defaultSide = 'before' } of texts) {
    let ctx = null;
    for (const raw of String(text || '').split('\n')) {
      const line = raw.trim();
      if (!line) continue;
      if (/^#{1,6}\s/.test(line)) {
        ctx = sideOf(line) || (SOFT_AFTER.test(line) ? 'after' : (SOFT_BEFORE.test(line) ? 'before' : null));
        continue;
      }
      const ids = idsIn(line);
      if (!ids.length) continue;
      const want = sideOf(line) || ctx || defaultSide;
      let side = want;
      const other = side === 'after' ? 'before' : 'after';
      const inSide = ids.filter(id => have[side].has(id));
      if (!inSide.length && ids.some(id => have[other].has(id))) side = other;
      const hit = ids.filter(id => have[side].has(id));
      const clean = line.replace(/^(?:[-*+]|\d+[.)])\s+/, '').replace(/\*\*/g, '');
      const short = clean.length > MAX_NOTE ? `${clean.slice(0, MAX_NOTE)}…` : clean;
      if (!hit.length) { issues.push({ kind: 'none', text: short, ids, side: want }); continue; }
      if (hit.length < ids.length) issues.push({ kind: 'partial', text: short, ids: ids.filter(id => !hit.includes(id)), side });
      if (side !== want) issues.push({ kind: 'moved', text: short, ids: hit, side });
      const no = notes.length + 1;
      notes.push({ no, side, ids: hit, text: short });
      for (const id of hit) marks[side].set(id, [...(marks[side].get(id) || []), no]);
    }
  }
  return { notes, marks, issues };
}

/** 계획 글의 표시된 줄 끝에 `◀ [1][3]` 를 붙인다 */
export function markPlan(planText, marks) {
  if (!marks || !marks.size) return planText;
  return String(planText || '').split('\n').map((l) => {
    const m = ROW_ID.exec(l);
    const nos = m ? marks.get(Number(m[1])) : null;
    return nos ? `${l}  ◀ ${nos.map(n => `[${n}]`).join('')}` : l;
  }).join('\n');
}
